import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CameraIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const IdentityVerification = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [uploads, setUploads] = useState({
    dlFront: null,
    dlBack: null,
    selfie: null
  });
  const [previews, setPreviews] = useState({
    dlFront: null,
    dlBack: null,
    selfie: null
  });
  const [loading, setLoading] = useState(false);

  const frontInputRef = useRef(null);
  const backInputRef = useRef(null);
  const selfieInputRef = useRef(null);
  const navigate = useNavigate();
  const { api } = useAuth();

  // Scroll to top whenever currentStep changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Automatically scroll to top when loading starts
  useEffect(() => {
    if (loading) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [loading]);

  const handleFileUpload = (type, file) => {
    if (file) {
      setUploads(prev => ({ ...prev, [type]: file }));
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviews(prev => ({ ...prev, [type]: e.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadDocuments = async () => {
    setLoading(true);
    
    try {
      const formData = new FormData();
      
      // Add files to FormData
      if (uploads.dlFront) formData.append('dlFront', uploads.dlFront);
      if (uploads.dlBack) formData.append('dlBack', uploads.dlBack);
      if (uploads.selfie) formData.append('selfie', uploads.selfie);
      
      // Add document type
      if (selectedDocType) formData.append('documentType', selectedDocType);

      // Upload using fetch with proper headers
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://uscivilcourt.org/api';
      const response = await fetch(`${API_BASE_URL}/identity/upload-documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type for FormData, let browser set it
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success('Documents uploaded successfully! Redirecting to dashboard...');

        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        toast.error(data.message || 'Upload failed. Please try again.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canProceedFromStep4 = uploads.dlFront && uploads.dlBack;
  const canProceedFromStep5 = uploads.selfie;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-sm p-8 w-full max-w-md text-center">
          <div className="mb-6">
            <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Uploading documents...</h3>
            <p className="text-sm text-gray-600">Please wait while we process your documents</p>
          </div>
          <div className="flex justify-center">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative">
      {/* Background Image for Step 1 */}
      {currentStep === 1 && (
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')`
          }}
        />
      )}

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          
          {/* Step 1: Identity Verification Required */}
          {currentStep === 1 && (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <h2 className="text-2xl font-semibold text-[#0057a3] mb-6" style={{fontFamily: 'Source Serif Pro'}}>
                Identity Verification Required
              </h2>
              
              <div className="mb-6">
                <p className="text-gray-700 mb-4">
                  Juror Registration is complete.
                </p>
                <p className="text-gray-700 mb-4">
                  To comply with State & Federal regulations, please provide:
                </p>
                
                <div className="text-left space-y-2 mb-6">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-[#0057a3] rounded-full mr-3"></div>
                    <span className="text-gray-700">State Issued ID (both front and back)</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-[#0057a3] rounded-full mr-3"></div>
                    <span className="text-gray-700">A selfie in good lighting</span>
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 mb-6">
                  A mobile device is required to complete these steps. When you're ready, click Proceed.
                </p>
              </div>

              <button
                onClick={() => setCurrentStep(2)}
                className="w-full bg-[#0057a3] text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center justify-center"
              >
                Proceed
              </button>
            </div>
          )}

          {/* Step 2: Submission Guidelines */}
          {currentStep === 2 && (
            <div className="bg-white rounded-lg shadow-sm p-8">
              <div className="text-center mb-6">
                <img 
                  src="/images/hands.png" 
                  alt="Phone taking photo of ID"
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
              </div>

              <h2 className="text-2xl font-semibold text-[#0057a3] mb-6 text-center" style={{fontFamily: 'Source Serif Pro'}}>
                Submission Guidelines
              </h2>
              
              <div className="space-y-3 mb-8">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-[#0057a3] rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700">Be in an area without glare from sunlight or overhead lighting.</span>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-[#0057a3] rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700">Place ID on a flat, dark surface.</span>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-[#0057a3] rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700">Ensure all edges of your ID are visible before you take a picture.</span>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-[#0057a3] rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700">When taking picture, ensure your selfie is within the guidelines.</span>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-[#0057a3] rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700">Follow all on-screen instructions.</span>
                </div>
              </div>

              <button
                onClick={() => setShowModal(true)}
                className="w-full bg-[#0057a3] text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center justify-center"
              >
                Proceed
              </button>
            </div>
          )}

          {/* Step 4: Document Upload */}
          {currentStep === 4 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              {/* How-to GIF/Image */}
              <div className="text-center mb-6">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                  <img 
                    src="/images/lic.png"
                    alt="How to take ID photo"
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                </div>

                <div className="bg-[#0057a3] text-white py-2 px-4 rounded text-sm font-medium">
                  {selectedDocType === 'drivers' ? "Driver's License Selected" : selectedDocType === 'passport' ? "Passport Selected" : "National ID Card Selected"}
                </div>
              </div>

              <h2 className="text-xl font-semibold text-[#0057a3] mb-6 text-center" style={{fontFamily: 'Source Serif Pro'}}>
                {selectedDocType === 'drivers' ? "Driver's License Selected" : selectedDocType === 'passport' ? "Passport Selected" : "National ID Card Selected"}
              </h2>
              
              <p className="text-center text-gray-600 mb-6">Tap to add images of your document.</p>

              {/* Upload Areas */}
              <div className="space-y-4 mb-6">
                {/* Front Upload */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <input
                    ref={frontInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload('dlFront', e.target.files[0])}
                    className="hidden"
                  />
                  
                  {previews.dlFront ? (
                    <div className="relative">
                      <img src={previews.dlFront} alt="Document front" className="w-full h-32 object-cover rounded" />
                      <button
                        onClick={() => {
                          setUploads(prev => ({...prev, dlFront: null}));
                          setPreviews(prev => ({...prev, dlFront: null}));
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div onClick={() => frontInputRef.current?.click()} className="cursor-pointer">
                      <CameraIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-600">Tap to add the</p>
                      <p className="text-gray-600 font-semibold">FRONT</p>
                      <p className="text-gray-600">of your document</p>
                    </div>
                  )}
                </div>

                {/* Back Upload */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <input
                    ref={backInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload('dlBack', e.target.files[0])}
                    className="hidden"
                  />
                  
                  {previews.dlBack ? (
                    <div className="relative">
                      <img src={previews.dlBack} alt="Document back" className="w-full h-32 object-cover rounded" />
                      <button
                        onClick={() => {
                          setUploads(prev => ({...prev, dlBack: null}));
                          setPreviews(prev => ({...prev, dlBack: null}));
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div onClick={() => backInputRef.current?.click()} className="cursor-pointer bg-gray-500 text-white py-8 rounded">
                      <CameraIcon className="w-12 h-12 text-white mx-auto mb-2" />
                      <p className="text-white">Tap to add the</p>
                      <p className="text-white font-semibold">BACK</p>
                      <p className="text-white">of your document</p>
                    </div>
                  )}
                </div>
              </div>

              {canProceedFromStep4 && (
                <p className="text-center text-sm text-gray-600 mb-4">
                  If you approve the images, click Proceed.
                </p>
              )}

              <button
                onClick={() => setCurrentStep(5)}
                disabled={!canProceedFromStep4}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center ${
                  canProceedFromStep4 
                    ? 'bg-[#0057a3] text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Proceed
              </button>
            </div>
          )}

          {/* Step 5: Take Selfie */}
          {currentStep === 5 && (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <div className="mb-6">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                  <img 
                    src="/images/self.png"
                    alt="How to take ID photo"
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                </div>

                <h2 className="text-2xl font-semibold text-[#0057a3] mb-4" style={{fontFamily: 'Source Serif Pro'}}>
                  Take a Selfie
                </h2>
                <p className="text-gray-600">Provide a forward-facing headshot.</p>
              </div>

              {/* Selfie Upload Area */}
              <div className="mb-6">
                <input
                  ref={selfieInputRef}
                  type="file"
                  accept="image/*"
                  capture="user"
                  onChange={(e) => handleFileUpload('selfie', e.target.files[0])}
                  className="hidden"
                />
                
                {previews.selfie ? (
                  <div className="relative inline-block">
                    <img src={previews.selfie} alt="Selfie" className="w-48 h-48 object-cover rounded-lg mx-auto" />
                    <button
                      onClick={() => {
                        setUploads(prev => ({...prev, selfie: null}));
                        setPreviews(prev => ({...prev, selfie: null}));
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div 
                    onClick={() => selfieInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-8 cursor-pointer hover:border-blue-400 transition-colors"
                  >
                    <CameraIcon className="w-16 h-16 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-600">Tap to take selfie</p>
                  </div>
                )}
              </div>

              <button
                onClick={uploadDocuments}
                disabled={!canProceedFromStep5}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                  canProceedFromStep5
                    ? 'bg-[#0057a3] text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Proceed
              </button>
            </div>
          )}

          {/* Document Selection Modal - Matching Reference Design */}
          {showModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl w-full max-w-2xl mx-auto shadow-2xl">
                {/* Modal Header */}
                <div className="p-8 pb-4 text-center">
                  <h3 className="text-3xl font-bold text-gray-800 mb-3">
                    Select ID type
                  </h3>
                  <p className="text-gray-500 text-sm">
                    Financial regulations require us to verify your identity.
                  </p>
                </div>
                
                {/* Modal Body - Three Options in Grid */}
                <div className="px-8 pb-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {/* Passport Option */}
                    <button
                      onClick={() => {
                        setSelectedDocType('passport');
                        setShowModal(false);
                        setCurrentStep(4);
                      }}
                      className="relative bg-gray-50 hover:bg-gray-100 border-2 border-gray-200 hover:border-gray-300 rounded-xl p-6 transition-all flex flex-col items-center group"
                    >
                      <div className="w-20 h-20 mb-4 flex items-center justify-center">
                        <svg className="w-16 h-16 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <rect x="4" y="3" width="16" height="18" rx="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="9" r="2" strokeWidth="2"/>
                          <path d="M9 14h6" strokeWidth="2" strokeLinecap="round"/>
                          <path d="M9 17h6" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <span className="text-gray-800 font-semibold text-center">Passport</span>
                    </button>
                    
                    {/* Driver's License Option - Recommended */}
                    <button
                      onClick={() => {
                        setSelectedDocType('drivers');
                        setShowModal(false);
                        setCurrentStep(4);
                      }}
                      className="relative bg-white border-2 border-blue-500 hover:border-blue-600 rounded-xl p-6 transition-all flex flex-col items-center group shadow-md"
                    >
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                        • Recommended
                      </div>
                      <div className="w-20 h-20 mb-4 flex items-center justify-center">
                        <svg className="w-16 h-16 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <rect x="2" y="8" width="20" height="12" rx="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="8" cy="14" r="2" strokeWidth="2"/>
                          <path d="M13 12h5" strokeWidth="2" strokeLinecap="round"/>
                          <path d="M13 16h3" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <span className="text-gray-800 font-semibold text-center">Driver's License</span>
                    </button>
                    
                    {/* National ID Card Option */}
                    <button
                      onClick={() => {
                        setSelectedDocType('national-id');
                        setShowModal(false);
                        setCurrentStep(4);
                      }}
                      className="relative bg-gray-50 hover:bg-gray-100 border-2 border-gray-200 hover:border-gray-300 rounded-xl p-6 transition-all flex flex-col items-center group"
                    >
                      <div className="w-20 h-20 mb-4 flex items-center justify-center">
                        <svg className="w-16 h-16 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <rect x="3" y="7" width="18" height="13" rx="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="9" cy="13" r="2" strokeWidth="2"/>
                          <path d="M14 11h4" strokeWidth="2" strokeLinecap="round"/>
                          <path d="M14 15h3" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <span className="text-gray-800 font-semibold text-center">National ID Card</span>
                    </button>
                  </div>

                  {/* Privacy Notice */}
                  <div className="text-center text-xs text-gray-500 leading-relaxed px-4">
                    Your photo ID and actions captured during the ID verification process may constitute biometric data. Please see our{' '}
                    <a href="#" className="text-blue-600 hover:underline">Privacy Policy</a> for more information about how we store and use your biometric data. Select ID type to proceed.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IdentityVerification;